/**
 * Survey Completion Page
 * Shows success message and summary after completing a survey
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Clock, MessageSquare } from 'lucide-react';
import Button from '../../components/Button';
import { getSurveySession } from '../../utils/surveyApi';
import { isTimedOutCompletion, isUncreditedCompletion } from './surveyTimer';
import { UI_TEXT } from '../../config/ui-text';

interface SessionData {
  id: number;
  state: string;
  survey_id: number;
  responses_count?: number;
  completed_at?: string;
  created_at: string;
}

const SurveyComplete: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  /**
   * Whether the survey-wide hard stop ended this session rather than the respondent finishing it.
   *
   * The flag the hard stop has always written and nothing has ever read. A respondent cut off at
   * question 12 of 40 must not be told they completed the survey.
   */
  const timedOut = isTimedOutCompletion(searchParams);
  /**
   * ...and whether that stop was actually recorded as a completion.
   *
   * The timed-out line asserts "your answers have been saved", which is a claim about the
   * database, and it was made unconditionally. Two routes here make it false: /timer/expire
   * granting the stop but answering `credited: false` — a session that recorded nothing is
   * retired rather than paid for — and the ask dying outright (a 404, or a run of failures long
   * enough to bound it), which leaves the session `in_progress` for the abandonment sweep.
   * Neither carries a panel redirect, so a panel respondent who is told everything is fine has no
   * reason to chase the credit they just lost.
   */
  const uncredited = timedOut && isUncreditedCompletion(searchParams);
  const [session, setSession] = useState<SessionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSession = async () => {
      if (!sessionId) return;
      
      try {
        setIsLoading(true);
        const result = await getSurveySession(parseInt(sessionId));
        setSession(result.session);
      } catch (error) {
        console.error('Failed to load session:', error);
        setError(UI_TEXT.HUMAN_SURVEY.COMPLETE.MESSAGES.FAILED_TO_LOAD);
      } finally {
        setIsLoading(false);
      }
    };

    loadSession();
  }, [sessionId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{UI_TEXT.HUMAN_SURVEY.COMPLETE.MESSAGES.LOADING}</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{UI_TEXT.HUMAN_SURVEY.COMPLETE.TITLES.SESSION_NOT_FOUND}</h2>
          <p className="text-gray-600 mb-4">{error || UI_TEXT.HUMAN_SURVEY.COMPLETE.MESSAGES.SESSION_NOT_FOUND}</p>
          <Button onClick={() => navigate('/')}>{UI_TEXT.HUMAN_SURVEY.COMPLETE.BUTTONS.GO_HOME}</Button>
        </div>
      </div>
    );
  }

  return (
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-sm text-center p-8">
          {/* Amber clock rather than a green tick when the buzzer ended it — the one glance a
              respondent gives this page has to say which of the two happened. Amber, not red:
              running out of time is not a failure and is never framed as one. */}
          <div className={`w-16 h-16 ${timedOut ? 'bg-amber-100' : 'bg-green-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
            {timedOut
              ? <Clock className="w-8 h-8 text-amber-600" />
              : <Check className="w-8 h-8 text-green-600" />}
          </div>

          {/* Thank You Message */}
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {timedOut
              ? UI_TEXT.HUMAN_SURVEY.COMPLETE.TITLES.TIMED_OUT
              : UI_TEXT.HUMAN_SURVEY.COMPLETE.TITLES.THANK_YOU}
          </h1>

          <p className="text-lg text-gray-600 mb-6">
            {uncredited
              ? UI_TEXT.HUMAN_SURVEY.COMPLETE.MESSAGES.TIMED_OUT_UNCREDITED
              : timedOut
                ? UI_TEXT.HUMAN_SURVEY.COMPLETE.MESSAGES.TIMED_OUT
                : UI_TEXT.HUMAN_SURVEY.COMPLETE.MESSAGES.SUCCESS}
          </p>

          {/* Action Buttons */}
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              {UI_TEXT.HUMAN_SURVEY.COMPLETE.MESSAGES.CLOSE_TAB}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyComplete;